import { namespace } from 'wsm-tagging-utils';

const runPage = (win) => {
	namespace(win, 'DDC.tracking.framework.runtime');

	const {
		DDC: { tracking }
	} = win;

	/**
	 * The Page class is a collection of page related queries.
	 *
	 * @param {DataLayerHelper} dataLayerHelper The DataLayerHelper used to access data layer properties
	 * @constructor
	 */
	tracking.framework.runtime.Page = function Page(dataLayerHelper) {
		this.dataLayerHelper = dataLayerHelper;
		this.formMapping = new Map();
	};

	tracking.framework.runtime.Page.prototype = {
		/**
		 * The default constructor
		 */
		constructor: win.DDC.tracking.framework.runtime.Page,

		/**
		 * Determines if the specified page alias/id refers to page type passed in test
		 *
		 * @alias alias The current DDC page alias
		 * @test function used to find page type
		 * @returns {boolean} true if matches the specified vehicle page; false otherwise
		 */
		isPageType(test, alias) {
			let newAlias = alias;
			if (newAlias === undefined) {
				newAlias = this.currentPageAlias();
				if (newAlias !== undefined && test(newAlias)) {
					return true;
				}
				newAlias = this.currentPageId();
				return newAlias !== undefined && test(newAlias);
			}
			return test.call(this, newAlias);
		},

		/**
		 * Determines if the specified page alias refers to a new vehicle (VLP or VDP)
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a new vehicle page; false otherwise
		 */
		isNewVehiclePage(alias) {
			return this.isPageType(
				(testAlias) => testAlias.indexOf('_NEW') > -1,
				alias
			);
		},

		/**
		 * Determines if the specified page alias refers to a certified used vehicle (VLP or VDP)
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a certified used vehicle page; false otherwise
		 */
		isCertifiedUsedVehiclePage(alias) {
			return this.isPageType(
				(testAlias) => testAlias.indexOf('_CERTIFIED_USED') > -1,
				alias
			);
		},

		/**
		 * Determines if the specified page alias refers to a used vehicle (VLP or VDP)
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a used vehicle page; false otherwise
		 */
		isUsedVehiclePage(alias) {
			return this.isPageType(
				(testAlias) => testAlias.indexOf('_USED') > -1,
				alias
			);
		},

		/**
		 * Determines if the current page being viewed is the Index page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is the Index page; false otherwise
		 */
		isIndexPage(alias) {
			return this.isPageType((testAlias) => testAlias === 'INDEX', alias);
		},

		/**
		 * Determines if the current page being viewed is the Vehicle Listing page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is the Vehicle Listing page; false otherwise
		 */
		isVehicleListingPage(alias) {
			return this.isPageType((testAlias) => {
				return testAlias.indexOf('INVENTORY_LISTING') > -1;
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is the Vehicle Detail page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is the Vehicle Detail page; false otherwise
		 */
		isVehicleDetailPage(alias) {
			const aliasToUse = alias || this.currentPageAlias();

			return this.isPageType((testAlias) => {
				return (
					testAlias.indexOf('AUTO_') === 0 &&
					testAlias.indexOf('_DETAILS') > -1
				);
			}, aliasToUse);
		},

		/**
		 * Determines if the current page being viewed is the Incentives page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is the Incentives page; false otherwise
		 */
		isIncentivesPage(alias) {
			return this.isPageType((testAlias) => {
				return testAlias.indexOf('INCENTIVES') > -1;
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is the Hours / Directions page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is the Hours / Directions page; false otherwise
		 */
		isHoursOrDirectionsPage(alias) {
			return this.isPageType((testAlias) => {
				return (
					testAlias.indexOf('HOURS') > -1 ||
					testAlias.indexOf('DIRECTIONS') > -1
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a dealership page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a Dealership page; false otherwise
		 */
		isAboutOrStaffPage(alias) {
			return this.isPageType(
				(testAlias) =>
					testAlias.indexOf('ABOUT') > -1 ||
					testAlias.indexOf('STAFF_LISTING') > -1 ||
					testAlias.indexOf('EMPLOYMENT_LISTING') > -1,
				alias
			);
		},

		/**
		 * Determines if the current page being viewed is a Contact page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a Contact page; false otherwise
		 */
		isContactPage(alias) {
			return this.isPageType((testAlias) => {
				return testAlias.indexOf('CONTACT') > -1;
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a compare page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a Compare page; false otherwise
		 */
		isComparePage(alias) {
			return this.isPageType((testAlias) => {
				return testAlias.indexOf('INVENTORY_COMPARE') > -1;
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a trade-in page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a trade-in page; false otherwise
		 */
		isTradeInPage(alias) {
			return this.isPageType((testAlias) => {
				return (
					testAlias.indexOf('TRADE') > -1 &&
					!(testAlias.indexOf('CONFIRM') > -1)
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a tool page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a Tool page; false otherwise
		 */
		isToolPage(alias) {
			return this.isPageType(function isCompareOrTradeInPage(testAlias) {
				return (
					this.isComparePage(testAlias) ||
					testAlias.indexOf('CALCULATOR_PAYMENT') > -1 ||
					this.isTradeInPage(testAlias) ||
					testAlias.indexOf('INVENTORY_FINDER') > -1
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a offers page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a offers page; false otherwise
		 */
		isOffersPage(alias) {
			// Exclude SPECIALS pages for PARTS as they are part of the Serivce pages
			return this.isPageType(function isPartsSpecialsOrCouponPage(
				testAlias
			) {
				return (
					(testAlias.indexOf('PARTS') === -1 &&
						testAlias.indexOf('SPECIALS') > -1) ||
					testAlias.indexOf('COUPON') > -1 ||
					this.isIncentivesPage(alias)
				);
			},
			alias);
		},

		/**
		 * Determines if the current page being viewed is a service page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a service page; false otherwise
		 */
		isServicePage(alias) {
			return this.isPageType(function isSomeTypeOfServicePage(testAlias) {
				return (
					(!this.isOffersPage(testAlias) &&
						testAlias.indexOf('SERVICE') > -1) ||
					testAlias.indexOf('PARTS') > -1 ||
					testAlias.indexOf('MOPAR') > -1 ||
					testAlias.indexOf('XTIME') > -1
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a financing page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a financing page; false otherwise
		 */
		isFinancePage(alias) {
			return this.isPageType(function isSomeTypeOfFinancepage(testAlias) {
				return (
					(!this.isOffersPage(testAlias) &&
						testAlias.indexOf('FINANCE') > -1) ||
					testAlias.indexOf('FINANCING') > -1
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a corporate page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a corporate page; false otherwise
		 */
		isCorporatePage(alias) {
			return this.isPageType((testAlias) => {
				return (
					testAlias.indexOf('PRIVACY') > -1 ||
					testAlias.indexOf('LEGAL') > -1 ||
					testAlias.indexOf('SITEMAP') > -1
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is a brochure page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is a brochure page; false otherwise
		 */
		isBrochurePage(alias) {
			return this.isPageType((testAlias) => {
				return (
					testAlias.indexOf('BROCHURE') > -1 ||
					testAlias.indexOf('POCKET_GUIDE') > -1
				);
			}, alias);
		},

		/**
		 * Determines if the current page being viewed is the Confirmation page
		 *
		 * @param alias The DDC page alias
		 * @returns {boolean} true if the current page is the Confirmation page; false otherwise
		 */
		isConfirmationPage(alias) {
			return alias.indexOf('CONFIRM') > -1;
		},

		/**
		 * Determines if the specified page alias refers to a Error Page (Page Not Found - 404)
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a 404 Error page; false otherwise
		 */
		isErrorPage(alias) {
			return alias.indexOf('404') > -1;
		},

		/**
		 * Determines if the specified page alias refers to a No Results Page
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a No Results page; false otherwise
		 */
		isNoResultsPage(alias) {
			return this.isPageType((testAlias) => {
				return testAlias.indexOf('NO_RESULTS') > -1;
			}, alias);
		},

		/**
		 * Determines if the specified page alias refers to a Model Showroom page
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a Model Showroom page; false otherwise
		 */
		isModelShowroomPage() {
			return this.dataLayerProperty('page.pageInfo.isShowroomOverview');
		},

		/**
		 * Determines if the specified page alias refers to a Video Gallery page
		 *
		 * @param alias The current DDC page alias
		 * @returns {boolean} true if a Video Gallery page; false otherwise
		 */
		isVideoGalleryPage(alias) {
			return this.isPageType((testAlias) => {
				return testAlias.indexOf('VIDEO_GALLERY') > -1;
			}, alias);
		},

		/**
		 * Returns the value of the specified data layer property
		 *
		 * @param name The name of the data layer property to lookup
		 * @returns {*} The value of the specified data layer property
		 */
		dataLayerProperty(name) {
			return this.dataLayerHelper.get(name);
		},

		/**
		 * Returns the DDC alias of the current page
		 *
		 * @returns {string} The DDC alias of the current page
		 */
		currentPageAlias() {
			return this.dataLayerProperty('page.pageInfo.pageName');
		},

		/**
		 * Returns the DDC id of the current page
		 *
		 * @returns {string} The DDC id of the current page
		 */
		currentPageId() {
			return this.dataLayerProperty('page.pageInfo.pageId');
		},

		/**
		 * Tests for the presence of a given widget in the current page using the
		 * 'page.pageInfo.pageWidgets' DDC.dataLayer property
		 *
		 * @param regex The regular expression object used to test against each
		 *              widget on the page
		 * @returns {boolean} true if the specified widget is present; false otherwise
		 */
		widgetIsPresent(regex) {
			const pageWidgets = this.dataLayerProperty(
				'page.pageInfo.pageWidgets'
			);
			return pageWidgets
				? Object.values(pageWidgets).some((value) => regex.test(value))
				: false;
		},

		/**
		 * Tests for the presence of at least one query facet in the supplied query
		 * facet collection.
		 *
		 * @param queryFacets The collection of query facets
		 * @returns {boolean} true if at least one is present; false otherwise
		 */
		queryFacetIsPresent(queryFacets) {
			return queryFacets
				? Object.values(queryFacets).some(
						(queryFacet) => queryFacet !== undefined
				  )
				: false;
		},

		setLocalStorage(storageKey, eventData) {
			localStorage.setItem(storageKey, JSON.stringify(eventData));
		},

		getLocalStorage(storageKey) {
			const data = localStorage.getItem(storageKey);

			// If data does not exist return without taking action
			if (!data) {
				return data;
			}

			const eventData = JSON.parse(data);

			if (eventData.element) {
				// Reconstitute element so it can be passed into the event.
				// Passing the element allows for further logic to be performed in the handler if necessary.
				const div = document.createElement('div');
				div.innerHTML = eventData.element;
				eventData.element = div.firstChild;
			}

			return eventData;
		},

		removeLocalStorage(storageKey) {
			localStorage.removeItem(storageKey);
		}
	};
};

export default runPage;
